import apiServices from './apiServices';
import {
  GetStewardReportHeadersRequest,
  GetStewardReportHeaderRequest,
  GetStewardReportDetailsRequest,
  CreateStewardReportHeaderRequest,
  UpdateStewardReportHeaderRequest,
  FinalizeStewardReportHeaderRequest,
  CreateStewardReportDetailRequest,
  DeleteStewardReportDetailRequest,
  SearchMemberRequest,
  GetJobRequest,
  GetAuthorizedJobsRequest,
  StewardReportHeader,
  StewardReportDetail,
  MemberSearchResult,
  JobInfo,
} from './dtos';

class stewardReportServices {
  baseSvc = new apiServices();
  client = this.baseSvc.client;

  constructor() {}

  /**
   * Get list of steward report headers
   * @param startDate Optional start date filter (ISO 8601)
   * @param endDate Optional end date filter (ISO 8601)
   * @returns Array of steward report headers
   */
  getHeaders(startDate?: string, endDate?: string): Promise<StewardReportHeader[]> {
    const request = new GetStewardReportHeadersRequest();
    if (startDate) request.startDate = startDate;
    if (endDate) request.endDate = endDate;
    return this.client.get(request);
  }

  /**
   * Get a single steward report header by ID
   * @param headerId The steward report header ID
   * @returns The steward report header
   */
  getHeader(headerId: number): Promise<StewardReportHeader> {
    const request = new GetStewardReportHeaderRequest();
    request.id = headerId;
    return this.client.get(request);
  }

  /**
   * Get report details for a specific header
   * @param headerId The steward report header ID
   * @returns Array of steward report details
   */
  getDetails(headerId: number): Promise<StewardReportDetail[]> {
    const request = new GetStewardReportDetailsRequest();
    request.id = headerId;
    return this.client.get(request);
  }

  /**
   * Create a new steward report header
   * @param headerData Partial header data (stewardUniqueMemberId optional, defaults to logged-in user)
   * @returns Created steward report header
   * Note: Details are prepopulated on the backend but not returned. Use getDetails() after creation.
   */
  createHeader(headerData: Partial<CreateStewardReportHeaderRequest>): Promise<StewardReportHeader> {
    const request = new CreateStewardReportHeaderRequest();
    Object.assign(request, headerData);
    return this.client.post(request);
  }

  /**
   * Update an existing steward report header
   * @param headerId The steward report header ID
   * @param headerData Partial header data (only provided fields are updated)
   * @returns Updated steward report header
   */
  updateHeader(
    headerId: number,
    headerData: Partial<UpdateStewardReportHeaderRequest>
  ): Promise<StewardReportHeader> {
    const request = new UpdateStewardReportHeaderRequest();
    request.id = headerId;
    Object.assign(request, headerData);
    return this.client.put(request);
  }

  /**
   * Finalize a steward report header (sets status to "Submitted" and locks it)
   * @param headerId The steward report header ID
   * @returns Finalized steward report header
   */
  finalizeHeader(headerId: number): Promise<StewardReportHeader> {
    const request = new FinalizeStewardReportHeaderRequest();
    request.id = headerId;
    return this.client.patch(request);
  }

  /**
   * Create or update a steward report detail (upsert)
   * If detailData.id is provided, updates existing record
   * If detailData.id is null/undefined, creates new record
   * @param detailData Detail data (stewardReportId and uniqueMemberId required, id optional for update)
   * @returns Created or updated steward report detail
   */
  upsertDetail(request: CreateStewardReportDetailRequest): Promise<StewardReportDetail> {
    // Request must be a proper DTO instance
    if (!(request instanceof CreateStewardReportDetailRequest)) {
      throw new Error('upsertDetail requires a CreateStewardReportDetailRequest instance');
    }
    return this.client.post(request);
  }

  /**
   * @deprecated Use upsertDetail instead. This method is kept for backward compatibility.
   * Create a new steward report detail
   */
  createDetail(detailData: Partial<CreateStewardReportDetailRequest>): Promise<StewardReportDetail> {
    return this.upsertDetail(detailData);
  }

  /**
   * @deprecated Use upsertDetail instead. This method is kept for backward compatibility.
   * Update an existing steward report detail
   */
  updateDetail(
    detailId: number,
    detailData: Partial<CreateStewardReportDetailRequest>
  ): Promise<StewardReportDetail> {
    return this.upsertDetail({ ...detailData, id: detailId });
  }

  /**
   * Delete all steward report details for a worker
   * @param stewardReportId The steward report header ID
   * @param uniqueMemberId The unique member ID
   * @returns Promise that resolves when deletion is complete
   */
  deleteDetail(stewardReportId: number, uniqueMemberId: string): Promise<void> {
    const request = new DeleteStewardReportDetailRequest();
    request.stewardReportId = stewardReportId;
    request.uniqueMemberId = uniqueMemberId;
    return this.client.delete(request);
  }

  /**
   * Search for members
   * @param lastName Optional partial last name match
   * @param last4SSN Optional last 4 digits of SSN
   * @returns Array of member search results (max 50)
   */
  searchMembers(lastName?: string, last4SSN?: string): Promise<MemberSearchResult[]> {
    const request = new SearchMemberRequest();
    if (lastName) request.lastName = lastName;
    if (last4SSN) request.last4SSN = last4SSN;
    return this.client.get(request);
  }

  /**
   * Get job information
   * @param jobId The job ID
   * @returns Job information including employer, payroll company, and report-to locations/persons
   */
  getJobInfo(jobId: string): Promise<JobInfo> {
    const request = new GetJobRequest();
    request.jobId = jobId;
    return this.client.get(request);
  }

  /**
   * Get members assigned to a job
   * @param jobId The job ID
   * @returns Array of job member information (uniqueMemberId and name)
   * @deprecated This method requires GetJobMembersRequest and JobMemberInfo DTOs to be added to dtos.ts
   */
  // getJobMembers(jobId: string): Promise<JobMemberInfo[]> {
  //   const request = new GetJobMembersRequest();
  //   request.jobId = jobId;
  //   return this.client.get(request);
  // }

  /**
   * Get list of authorized jobs for the current user
   * @returns Array of authorized job information
   */
  getAuthorizedJobs(): Promise<JobInfo[]> {
    const request = new GetAuthorizedJobsRequest();
    return this.client.get(request);
  }
}

export default stewardReportServices;

